import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface Option {
    id: string;
    label: string;
}

interface MultiSelectDropdownProps {
    label: string;
    options: Option[];
    selectedIds: string[];
    onToggle: (id: string) => void;
    placeholder?: string;
    emptyLabel?: string;
}

export function MultiSelectDropdown({
    label,
    options,
    selectedIds,
    onToggle,
    placeholder = 'Sélectionner...',
    emptyLabel = 'Aucune option disponible',
}: MultiSelectDropdownProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selectedLabels = options.filter((o) => selectedIds.includes(o.id)).map((o) => o.label);

    return (
        <div ref={ref} className="relative">
            <label className="field-label">{label}</label>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="input flex items-center justify-between gap-2 text-left"
            >
                <span className="flex flex-wrap gap-1 min-w-0">
                    {selectedLabels.length === 0 ? (
                        <span className="text-[var(--color-text-muted)]">{placeholder}</span>
                    ) : (
                        selectedLabels.map((l) => (
                            <span key={l} className="badge badge-default">{l}</span>
                        ))
                    )}
                </span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[var(--color-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto card p-1 shadow-lg">
                    {options.length === 0 && (
                        <p className="text-sm text-[var(--color-text-muted)] px-2 py-1">{emptyLabel}</p>
                    )}
                    {options.map((o) => (
                        <label
                            key={o.id}
                            className="flex items-center gap-2 text-sm text-[var(--color-text)] px-2 py-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(o.id)}
                                onChange={() => onToggle(o.id)}
                            />
                            {o.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}
